import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Filter, RotateCcw, ChevronDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface ProductFiltersProps {
  onFilterChange: (filters: any) => void;
  onReset?: () => void;
  brands?: any[];
  merchants?: any[];
  searchType: string;
  setSearchType: (type: string) => void;
  searchTerm: string;
  onSearch: (term: string) => void;
}

const FILTER_CATEGORIES = [
  { label: "Brand", value: "brand", isArray: false },
  { label: "Merchant", value: "merchant", isArray: false },
  { label: "Platform Category", value: "platformCategory", isArray: false },
];

export const ProductFilters: React.FC<ProductFiltersProps> = ({
  onFilterChange,
  onReset,
  brands = [],
  merchants = [],
  searchType,
  setSearchType,
  searchTerm,
  onSearch,
}) => {
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("brand");
  const [filterSelections, setFilterSelections] = useState<any>({
    brand: "",
    merchant: "",
    platformCategory: "",
  });
  const [pendingFilterSelections, setPendingFilterSelections] = useState<any>({
    brand: "",
    merchant: "",
    platformCategory: "",
  });
  const [searchInputs, setSearchInputs] = useState<any>({
    brand: "",
    merchant: "",
    platformCategory: "",
  });

  // Count of active filters
  const activeFilterCount = ["brand", "merchant", "platformCategory"].reduce(
    (acc, key) => {
      return acc + (filterSelections[key] ? 1 : 0);
    },
    0
  );

  useEffect(() => {
    if (filterPopoverOpen) {
      setPendingFilterSelections({ ...filterSelections });
    }
  }, [filterPopoverOpen]);

  // Reset internal state when searchTerm is cleared (reset button clicked)
  useEffect(() => {
    if (searchTerm === "") {
      setFilterSelections({
        brand: "",
        merchant: "",
        platformCategory: "",
      });
      setPendingFilterSelections({
        brand: "",
        merchant: "",
        platformCategory: "",
      });
      setSearchInputs({
        brand: "",
        merchant: "",
        platformCategory: "",
      });
    }
  }, [searchTerm]);

  const applyFilters = () => {
    setFilterSelections({ ...pendingFilterSelections });
    const filterPayload: any = {};

    // Find selected brand's id
    if (pendingFilterSelections.brand) {
      const selectedBrand = brands.find(
        (b: any) => b.brandCode === pendingFilterSelections.brand
      );
      if (selectedBrand && selectedBrand.id) {
        filterPayload.brandId = selectedBrand.id;
      }
    }

    // Find selected merchant's id
    if (pendingFilterSelections.merchant) {
      const selectedMerchant = merchants.find((m: any) => {
        const merchantCode =
          m.merchantCode || m.code || m.merchantCodeVO?.merchantCode;
        return merchantCode === pendingFilterSelections.merchant;
      });
      if (selectedMerchant && selectedMerchant.id) {
        filterPayload.redemptionMerchantId = selectedMerchant.id;
      }
    }

    // Platform category
    if (pendingFilterSelections.platformCategory) {
      filterPayload.platformCategoryId =
        pendingFilterSelections.platformCategory;
    }

    onFilterChange(filterPayload);
    setFilterPopoverOpen(false);
  };

  const handleOptionSelect = (category: string, value: string) => {
    setPendingFilterSelections((prev) => ({ ...prev, [category]: value }));
  };

  const handleSearchInput = (category: string, value: string) => {
    setSearchInputs((prev) => ({ ...prev, [category]: value }));
  };

  const handleClearAll = () => {
    setFilterSelections({ brand: "", merchant: "", platformCategory: "" });
    setPendingFilterSelections({
      brand: "",
      merchant: "",
      platformCategory: "",
    });
    onFilterChange({});
  };

  const handleClearCurrent = () => {
    setPendingFilterSelections((prev) => ({ ...prev, [activeCategory]: "" }));
  };

  const renderOptions = () => {
    const searchTerm = searchInputs[activeCategory] || "";

    switch (activeCategory) {
      case "brand":
        const filteredBrands = brands.filter(
          (brand: any) =>
            brand.brandName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            brand.brandCode?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        return (
          <div className="space-y-1">
            {filteredBrands.map((brand: any) => (
              <button
                key={brand.id}
                className={`w-full text-left px-2 py-1 rounded text-sm hover:bg-gray-100 ${
                  pendingFilterSelections.brand === brand.brandCode
                    ? "bg-blue-100 text-blue-700"
                    : "text-gray-700"
                }`}
                onClick={() => handleOptionSelect("brand", brand.brandCode)}
              >
                <div className="font-medium">{brand.brandName}</div>
                <div className="text-xs text-gray-500">{brand.brandCode}</div>
              </button>
            ))}
            {pendingFilterSelections.brand && (
              <button
                className="text-xs text-gray-500 hover:underline mt-2 self-start"
                onClick={() => handleOptionSelect("brand", "")}
                type="button"
              >
                Clear
              </button>
            )}
          </div>
        );
      case "merchant":
        // Ensure merchants is an array and handle the API response structure
        const merchantsArray = Array.isArray(merchants)
          ? merchants
          : merchants && typeof merchants === "object"
          ? (merchants as any).redemptionMerchantVOs ||
            (merchants as any).data ||
            []
          : [];

        console.log("Merchants data:", merchants);
        console.log("Merchants array:", merchantsArray);
        console.log("Merchants array length:", merchantsArray.length);

        // Log the first few merchants to see their structure
        if (merchantsArray.length > 0) {
          console.log("First merchant structure:", merchantsArray[0]);
        }

        const filteredMerchants = merchantsArray.filter((merchant: any) => {
          const merchantName =
            merchant.merchantName ||
            merchant.name ||
            merchant.merchantNameVO?.merchantName;
          const merchantCode =
            merchant.merchantCode ||
            merchant.code ||
            merchant.merchantCodeVO?.merchantCode;

          return (
            merchantName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            merchantCode?.toLowerCase().includes(searchTerm.toLowerCase())
          );
        });

        console.log("Filtered merchants:", filteredMerchants);

        // If no merchants found, show a message or sample data
        if (filteredMerchants.length === 0) {
          return (
            <div className="space-y-1">
              <div className="text-sm text-gray-500 p-2">
                {merchantsArray.length === 0
                  ? "No merchants available"
                  : "No merchants match your search"}
              </div>
              {pendingFilterSelections.merchant && (
                <button
                  className="text-xs text-gray-500 hover:underline mt-2 self-start"
                  onClick={() => handleOptionSelect("merchant", "")}
                  type="button"
                >
                  Clear
                </button>
              )}
            </div>
          );
        }

        return (
          <div className="space-y-1">
            {filteredMerchants.map((merchant: any) => {
              const merchantName =
                merchant.merchantName ||
                merchant.name ||
                merchant.merchantNameVO?.merchantName;
              const merchantCode =
                merchant.merchantCode ||
                merchant.code ||
                merchant.merchantCodeVO?.merchantCode;

              return (
                <button
                  key={merchant.id}
                  className={`w-full text-left px-2 py-1 rounded text-sm hover:bg-gray-100 ${
                    pendingFilterSelections.merchant === merchantCode
                      ? "bg-blue-100 text-blue-700"
                      : "text-gray-700"
                  }`}
                  onClick={() => handleOptionSelect("merchant", merchantCode)}
                >
                  <div className="font-medium">{merchantName}</div>
                  <div className="text-xs text-gray-500">{merchantCode}</div>
                </button>
              );
            })}
            {pendingFilterSelections.merchant && (
              <button
                className="text-xs text-gray-500 hover:underline mt-2 self-start"
                onClick={() => handleOptionSelect("merchant", "")}
                type="button"
              >
                Clear
              </button>
            )}
          </div>
        );
      case "platformCategory":
        const platformCategories = [
          { id: 1, name: "Electronics" },
          { id: 2, name: "Fashion" },
          { id: 3, name: "Home & Garden" },
          { id: 4, name: "Sports & Outdoors" },
          { id: 5, name: "Books & Media" },
          { id: 6, name: "Health & Beauty" },
          { id: 7, name: "Toys & Games" },
          { id: 8, name: "Automotive" },
        ];
        const filteredCategories = platformCategories.filter((category: any) =>
          category.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        return (
          <div className="space-y-1">
            {filteredCategories.map((category: any) => (
              <button
                key={category.id}
                className={`w-full text-left px-2 py-1 rounded text-sm hover:bg-gray-100 ${
                  pendingFilterSelections.platformCategory === category.id
                    ? "bg-blue-100 text-blue-700"
                    : "text-gray-700"
                }`}
                onClick={() =>
                  handleOptionSelect("platformCategory", category.id)
                }
              >
                {category.name}
              </button>
            ))}
            {pendingFilterSelections.platformCategory && (
              <button
                className="text-xs text-gray-500 hover:underline mt-2 self-start"
                onClick={() => handleOptionSelect("platformCategory", "")}
                type="button"
              >
                Clear
              </button>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Card className="mb-6 border-0 shadow-sm">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center justify-between mb-2 gap-3">
          {/* Left: Search dropdown, input */}
          <div className="flex gap-3 items-center">
            <Select value={searchType} onValueChange={setSearchType}>
              <SelectTrigger className="h-10 w-56">
                <SelectValue placeholder="Product Code" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="productCode">Product Code</SelectItem>
                <SelectItem value="productName">Product Name</SelectItem>
              </SelectContent>
            </Select>
            <Input
              className="w-64"
              type="text"
              placeholder={
                searchType === "productCode"
                  ? "Search by Product Code"
                  : "Search by Product Name"
              }
              value={searchTerm}
              onChange={(e) => onSearch(e.target.value)}
            />
          </div>
          {/* Right: Filter, Reset */}
          <div className="flex gap-2 items-center ml-auto">
            <Popover
              open={filterPopoverOpen}
              onOpenChange={setFilterPopoverOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  variant={activeFilterCount > 0 ? "default" : "outline"}
                  className="flex items-center gap-2 px-3 py-2 text-sm"
                  onClick={() => setFilterPopoverOpen(true)}
                  size="sm"
                >
                  <Filter className="h-4 w-4" />
                  Filter
                  {activeFilterCount > 0 && (
                    <span className="ml-1 bg-blue-600 text-white rounded-full px-2 py-0.5 text-xs font-semibold">
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[340px] p-0" align="end">
                <div className="flex max-h-[320px] h-[320px]">
                  {/* Left: Categories */}
                  <div className="w-32 border-r bg-gray-50 p-2 flex flex-col gap-1">
                    {FILTER_CATEGORIES.map((cat) => (
                      <button
                        key={cat.value}
                        className={`text-left px-2 py-1 rounded hover:bg-gray-100 font-medium text-xs ${
                          activeCategory === cat.value
                            ? "bg-white shadow text-blue-600"
                            : "text-gray-700"
                        }`}
                        onClick={() => setActiveCategory(cat.value)}
                      >
                        {cat.label}
                        {cat.isArray &&
                        Array.isArray(filterSelections[cat.value]) &&
                        filterSelections[cat.value].length > 0 ? (
                          <span className="ml-2 bg-blue-100 text-blue-700 rounded-full px-1.5 py-0.5 text-[10px] font-semibold">
                            {filterSelections[cat.value].length}
                          </span>
                        ) : null}
                      </button>
                    ))}
                    <div className="flex-1" />
                    <button
                      className="text-[10px] text-gray-500 hover:underline mt-2"
                      onClick={handleClearAll}
                      type="button"
                    >
                      Clear all
                    </button>
                  </div>
                  {/* Right: Options for selected category */}
                  <div className="flex-1 p-2 flex flex-col min-h-0">
                    <div className="flex items-center mb-1">
                      {activeCategory !== "status" && (
                        <Input
                          placeholder={`Search ${activeCategory}`}
                          value={searchInputs[activeCategory] || ""}
                          onChange={(e) =>
                            handleSearchInput(activeCategory, e.target.value)
                          }
                          className="h-8 text-xs"
                        />
                      )}
                      {pendingFilterSelections[activeCategory] && (
                        <button
                          className="text-[10px] text-gray-500 hover:underline ml-2"
                          onClick={handleClearCurrent}
                          type="button"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="flex-1 overflow-y-auto">
                      {renderOptions()}
                    </div>
                  </div>
                </div>
                <div className="border-t p-2 flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFilterPopoverOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button size="sm" onClick={applyFilters}>
                    Apply
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            <Button
              variant="outline"
              size="sm"
              onClick={onReset}
              className="flex items-center gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
